import React, { useState, useMemo } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useCompanyIncomeStatement } from "../hooks/useCompanyIncomeStatement";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatNumber } from "../utils/formatNumber";
import { TransformedDataPoint } from "../utils/chartDataTransformers";
import {
  ViewMode,
  DEFAULT_QUARTERLY_LIMIT,
  MAX_QUARTERLY_LIMIT,
  MAX_ANNUAL_LIMIT,
  safeSlice,
  getApiPeriodParam,
} from "./shared/financialPeriodConfig";
import GenericFinancialBarChart from "./shared/GenericFinancialBarChart";
import ChartToggleButton from "./shared/ChartToggleButton";
import ChartTooltip from "./shared/ChartTooltip";

dayjs.extend(quarterOfYear);

const EXPENSE_CATEGORIES = [
  {
    key: "researchAndDevelopment",
    label: "Research & Development",
    shortLabel: "R&D",
    color: "#3b82f6",
    field: "researchAndDevelopmentExpensesActual",
  },
  {
    key: "generalAndAdministrative",
    label: "General & Administrative",
    shortLabel: "G&A",
    color: "#ef4444",
    field: "generalAndAdministrativeExpensesActual",
  },
  {
    key: "sellingAndMarketing",
    label: "Selling & Marketing",
    shortLabel: "S&M",
    color: "#22c55e",
    field: "sellingAndMarketingExpensesActual",
  },
  {
    key: "sellingGeneralAndAdministrative",
    label: "SG&A",
    shortLabel: "SG&A",
    color: "#f59e0b",
    field: "sellingGeneralAndAdministrativeExpensesActual",
  },
  {
    key: "otherExpenses",
    label: "Other Expenses",
    shortLabel: "Other",
    color: "#8b5cf6",
    field: "otherExpensesActual",
  },
  {
    key: "operatingExpenses",
    label: "Operating Expenses",
    shortLabel: "OpEx",
    color: "#06b6d4",
    field: "operatingExpensesActual",
  },
] as const;

const categoryColors = EXPENSE_CATEGORIES.reduce(
  (acc, cat) => {
    acc[cat.key] = cat.color;
    return acc;
  },
  {} as Record<string, string>
);

interface ExpensesChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  viewMode?: ViewMode;
  limit?: number;
}

const ExpensesChart: React.FC<ExpensesChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
  viewMode: viewModeProp,
  limit: limitProp,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { currencySymbol } = useStockCurrency();

  const [internalViewMode] = useState<ViewMode>("quarterly");
  const [internalLimit] = useState(DEFAULT_QUARTERLY_LIMIT);

  const effectiveViewMode = viewModeProp ?? internalViewMode;
  const effectiveLimit = limitProp ?? internalLimit;

  const period = getApiPeriodParam(effectiveViewMode);
  const maxLimit = effectiveViewMode === "quarterly" ? MAX_QUARTERLY_LIMIT : MAX_ANNUAL_LIMIT;
  const { data, isLoading, error } = useCompanyIncomeStatement(
    ticker,
    maxLimit,
    period
  );

  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(EXPENSE_CATEGORIES.map((cat) => cat.key))
  );

  const chartData = useMemo((): TransformedDataPoint[] => {
    if (!data || data.length === 0) return [];

    const validData = data.filter((item) =>
      EXPENSE_CATEGORIES.some(
        (cat) => item[cat.field as keyof typeof item] !== null
      )
    );

    if (effectiveViewMode === "quarterly") {
      const sortedData = validData.sort(
        (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
      );

      const periodCounts = new Map<string, number>();

      const result = sortedData.map((item) => {
        const basePeriod = `Q${dayjs(item.date).quarter()} ${dayjs(item.date).year()}`;
        const count = periodCounts.get(basePeriod) ?? 0;
        periodCounts.set(basePeriod, count + 1);
        const periodLabel =
          count > 0 ? `${basePeriod} (${count + 1})` : basePeriod;

        return {
          period: periodLabel,
          researchAndDevelopment:
            (item.researchAndDevelopmentExpensesActual || 0) / 1000000,
          generalAndAdministrative:
            (item.generalAndAdministrativeExpensesActual || 0) / 1000000,
          sellingAndMarketing:
            (item.sellingAndMarketingExpensesActual || 0) / 1000000,
          sellingGeneralAndAdministrative:
            (item.sellingGeneralAndAdministrativeExpensesActual || 0) / 1000000,
          otherExpenses: (item.otherExpensesActual || 0) / 1000000,
          operatingExpenses: (item.operatingExpensesActual || 0) / 1000000,
          date: item.date,
          year: dayjs(item.date).year(),
          quarter: dayjs(item.date).quarter(),
        };
      });

      return safeSlice(result, effectiveLimit);
    }

    const result = validData
      .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix())
      .map((item) => ({
        period: dayjs(item.date).year().toString(),
        researchAndDevelopment:
          (item.researchAndDevelopmentExpensesActual || 0) / 1000000,
        generalAndAdministrative:
          (item.generalAndAdministrativeExpensesActual || 0) / 1000000,
        sellingAndMarketing:
          (item.sellingAndMarketingExpensesActual || 0) / 1000000,
        sellingGeneralAndAdministrative:
          (item.sellingGeneralAndAdministrativeExpensesActual || 0) / 1000000,
        otherExpenses: (item.otherExpensesActual || 0) / 1000000,
        operatingExpenses: (item.operatingExpensesActual || 0) / 1000000,
        date: item.date,
        year: dayjs(item.date).year(),
        quarter: null,
      }));

    return safeSlice(result, effectiveLimit);
  }, [data, effectiveViewMode, effectiveLimit]);

  const categoryTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    EXPENSE_CATEGORIES.forEach((cat) => {
      totals[cat.key] = chartData.reduce(
        (sum, dataPoint) => sum + ((dataPoint[cat.key] as number) || 0),
        0
      );
    });
    return totals;
  }, [chartData]);

  const sortedCategories = useMemo(() => {
    return [...EXPENSE_CATEGORIES].sort(
      (a, b) => categoryTotals[b.key] - categoryTotals[a.key]
    );
  }, [categoryTotals]);

  const visibleKeys = sortedCategories
    .filter((cat) => visibleCategories.has(cat.key))
    .map((cat) => cat.key);

  const toggleCategory = (categoryKey: string) => {
    setVisibleCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  const chipLegend = (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        mt: noHover ? 0 : 2,
        justifyContent: "center",
        px: noHover ? 0 : 2,
        ...(noHover && {
          maxWidth: "750px",
          mx: "auto",
        }),
      }}
    >
      {sortedCategories.map((category) => {
        const label = isMobile || noHover ? category.shortLabel : category.label;
        return (
          <ChartToggleButton
            key={category.key}
            label={label}
            selected={visibleCategories.has(category.key)}
            onToggle={() => toggleCategory(category.key)}
            color={category.color}
          />
        );
      })}
    </Box>
  );

  return (
    <GenericFinancialBarChart
      title="Expense Breakdown"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      chartData={chartData}
      viewMode={effectiveViewMode}
      barKeys={visibleKeys}
      groupMode="stacked"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      colors={({ id }: any) => categoryColors[id as string]}
      minValue="auto"
      yAxisLegend="Expenses"
      yAxisFormat={(value) => {
        const absValue = Math.abs(value);
        const formattedValue = formatNumber(absValue * 1000000);
        return value < 0
          ? `-${currencySymbol}${formattedValue}`
          : `${currencySymbol}${formattedValue}`;
      }}
      noDataTitle="No Expenses Data Available"
      noDataDescription="Expenses data is not available for this company, which is common for smaller companies, recent IPOs, or international stocks."
      tooltipContent={({ id, value, indexValue, color }) => {
        const category = EXPENSE_CATEGORIES.find((cat) => cat.key === id);
        return (
          <ChartTooltip
            header={String(indexValue)}
            headerColor={color}
            rows={[{ label: category?.label ?? "", value: `${currencySymbol}${formatNumber((value as number) * 1000000, 2)}` }]}
          />
        );
      }}
      belowChartContent={chipLegend}
    />
  );
};

export default ExpensesChart;
